<template>
  <dashboard-layout title="Suggested Questions Management">
    <template #actions>
      <button class="add-btn" @click="showAddModal = true">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        New Question
      </button>
    </template>

    <stat-grid>
      <stat-card label="Total questions" :value="suggestions.length.toLocaleString('en-US')" icon="lightbulb" accent />
    </stat-grid>

    <data-panel flush>
      <template #title>Questions <span class="muted">({{ suggestions.length.toLocaleString('en-US') }})</span></template>

      <loading-state v-if="loading" label="Loading suggestions…" />
      <loading-state v-else-if="updatingOrder" label="Updating order…" />
      <data-table
        v-else
        :columns="columns"
        :rows="suggestions"
        row-key="id"
      >
        <template #cell-order="{ row }">
          <div
            class="dcell handle"
            :class="{ dragging: isDragging(row) }"
            draggable="true"
            @dragstart="onDragStart(row)"
            @dragend="onDragEnd"
            @dragover.prevent
            @drop.prevent="onDrop(row)"
          >
            <nav-icon name="menu" :size="14" class="grip" />
            <span>{{ row.order }}</span>
          </div>
        </template>
        <template #cell-createdAt="{ row }">
          <div class="dcell" :class="{ dragging: isDragging(row) }" @dragover.prevent @drop.prevent="onDrop(row)">
            {{ formatCreatedAt(row.createdAt) }}
          </div>
        </template>
        <template #cell-value="{ row }">
          <div class="dcell" :class="{ dragging: isDragging(row) }" @dragover.prevent @drop.prevent="onDrop(row)">
            {{ row.value }}
          </div>
        </template>
        <template #cell-actions="{ row }">
          <div class="dcell" :class="{ dragging: isDragging(row) }" @dragover.prevent @drop.prevent="onDrop(row)">
            <button class="delete-btn" @click="deleteSuggestion(row.id)">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </template>
        <template #empty>No suggestions found</template>
      </data-table>
    </data-panel>

    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-content">
        <h3>Add New Suggestion</h3>
        <textarea
          v-model="newSuggestionValue"
          placeholder="Enter suggestion question..."
          rows="4"
          class="suggestion-textarea"
        ></textarea>
        <div class="modal-actions">
          <button class="cancel-btn" @click="showAddModal = false">Cancel</button>
          <button
            class="save-btn"
            @click="saveSuggestion"
            :disabled="!newSuggestionValue.trim() || saving"
          >
            {{ saving ? 'Saving...' : 'Save Question' }}
          </button>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { FirebaseSuggestionService } from '../services/firebaseSuggestionService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import LoadingState from './ui/LoadingState.vue'
import NavIcon from './NavIcon.vue'

const columns = [
  { key: 'order', label: 'Order', width: '100px' },
  { key: 'createdAt', label: 'Created At', width: '170px' },
  { key: 'value', label: 'Question' },
  { key: 'actions', label: 'Actions', width: '90px', align: 'right' }
]

export default {
  name: 'SuggestionsManagement',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, LoadingState, NavIcon
  },
  setup() {
    const service = new FirebaseSuggestionService()
    const suggestions = ref([])
    const loading = ref(true)
    const showAddModal = ref(false)
    const newSuggestionValue = ref('')
    const saving = ref(false)
    const dragging = ref(false)
    const draggedItem = ref(null)
    const updatingOrder = ref(false)

    const loadSuggestions = async () => {
      try {
        suggestions.value = await service.getSuggestions()
      } catch (error) {
        console.error('Error loading suggestions:', error)
      } finally {
        loading.value = false
      }
    }

    const saveSuggestion = async () => {
      if (!newSuggestionValue.value.trim() || saving.value) return

      saving.value = true
      try {
        await service.addSuggestion(newSuggestionValue.value.trim())
        await loadSuggestions()
        showAddModal.value = false
        newSuggestionValue.value = ''
      } catch (error) {
        console.error('Error saving suggestion:', error)
      } finally {
        saving.value = false
      }
    }

    const deleteSuggestion = async (id) => {
      if (!confirm('Are you sure you want to delete this suggestion?')) return

      try {
        await service.deleteSuggestion(id)
        await loadSuggestions()
      } catch (error) {
        console.error('Error deleting suggestion:', error)
      }
    }

    const onDragStart = (suggestion) => {
      dragging.value = true
      draggedItem.value = suggestion
    }

    const onDragEnd = () => {
      dragging.value = false
      draggedItem.value = null
    }

    const onDrop = async (targetSuggestion) => {
      if (!draggedItem.value || draggedItem.value.id === targetSuggestion.id) return

      updatingOrder.value = true
      try {
        await service.updateOrder(
          draggedItem.value.id,
          targetSuggestion.order,
          draggedItem.value.order
        )
        await loadSuggestions()
      } catch (error) {
        console.error('Error updating order:', error)
      } finally {
        updatingOrder.value = false
      }
    }

    const isDragging = (suggestion) => dragging.value && draggedItem.value?.id === suggestion.id

    const formatCreatedAt = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp.seconds * 1000)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(() => {
      loadSuggestions()
    })

    return {
      suggestions,
      loading,
      showAddModal,
      newSuggestionValue,
      saving,
      updatingOrder,
      columns,
      loadSuggestions,
      saveSuggestion,
      deleteSuggestion,
      onDragStart,
      onDragEnd,
      onDrop,
      isDragging,
      formatCreatedAt
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }

.add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}

/* Drag-and-drop reorder cells: each cell fills its <td> so the whole row is a drop target */
.dcell {
  margin: -11px -14px;
  padding: 11px 14px;
}
.dcell.handle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: move;
}
.grip { color: var(--text-secondary); }
.dcell.dragging { opacity: 0.5; }

.delete-btn {
  background: none;
  border: none;
  color: #FF5252;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.delete-btn:hover {
  background: rgba(255, 82, 82, 0.1);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 15, 26, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: rgba(26, 27, 46, 0.95);
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-content h3 {
  margin: 0 0 16px 0;
  color: var(--text-primary);
}

.suggestion-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: var(--text-primary);
  font-size: 14px;
  resize: vertical;
  margin-bottom: 16px;
}

.suggestion-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-actions button {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.05);
}

.save-btn {
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  border: none;
  color: white;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}

.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
